import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';


// Componentes 


// Funciones para obtener datos
import ObtenerPartner from '../functions/ObtenerPartner';
import InsertarPartner from '../functions/InsertarPartner';
import GuardarPartner from '../functions/GuardarPartner';
import buildImageSrc from '../functions/buildImageSrc';

// Tipos de datos
import PartnersEditar from '../types/PartnersEditar';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarPartnerProps {
    idPartner: number;
    guardarCambios: (Partner: PartnersEditar) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}
  
export const EditarPartner: React.FC<EditarPartnerProps> = ({ idPartner, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [Partner, setPartner] = useState<PartnersEditar | null>(null);
    
    const [errorPartner, setErrorPartner] = useState<string | null>(null);

    const [localId, setLocalId] = useState(0);
    const [localCuit, setLocalCuit] = useState('');
    const [localRazonSocial, setLocalRazonSocial] = useState('');
    const [localDomicilio, setLocalDomicilio] = useState('');
    const [localTelefono, setLocalTelefono] = useState('');
    const [localWhatsapp, setLocalWhatsapp] = useState('');
    const [localWeb, setLocalWeb] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localMostrarWeb, setLocalMostrarWeb] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localLogoMime, setLocalLogoMime] = useState<string | null>(null);
    const [localLogoContent, setLocalLogoContent] = useState<string | null>(null);
    const [localLogoName, setLocalLogoName] = useState<string | null>(null);

    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);

    const fileToBase64 = (file: File): Promise<{base64: string; mime: string; name: string}> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = () => {
                const result = reader.result as string;
                const [meta, base64] = result.split(',');

                // meta = "data:image/png;base64"
                const mime = meta.split(':')[1].split(';')[0];

                resolve({ base64, mime, name: file.name });
            }

            reader.onerror = reject;
        });
    };

    useEffect(() => {
        
        const fetchPartner = async () => {
            try {

                const data = await ObtenerPartner(idPartner);

                if (data.partners_ok === 'si') {

                    // Todo ok, partner recuperado

                    const PartnerRecuperado: PartnersEditar = {
                        id: data.partners[0].id,
                        cuit: data.partners[0].cuit,
                        razonsocial: data.partners[0].razonsocial,
                        domicilio: data.partners[0].domicilio,
                        telefono: data.partners[0].telefono,
                        whatsapp: data.partners[0].whatsapp,
                        web: data.partners[0].web,
                        habilitado: data.partners[0].habilitado,
                        mostrar_web: data.partners[0].mostrar_web,
                        fchalta: data.partners[0].fchalta,
                        fchmodificado: data.partners[0].fchmodificado,
                        dado_baja: data.partners[0].dado_baja,
                        fchbaja: data.partners[0].fchbaja,
                        logo_mime: data.partners[0].logo_mime,
                        logo_content: data.partners[0].logo_content,
                        logo_name: data.partners[0].logo_name,
                        imageSrc: buildImageSrc(data.partners[0].logo_mime, data.partners[0].logo_content) || null

                    };

                    setImagePreview(PartnerRecuperado.imageSrc ?? null);

                    setPartner(PartnerRecuperado);

                    setLocalId(data.partners[0].id);
                    setLocalCuit(data.partners[0].cuit);
                    setLocalRazonSocial(data.partners[0].razonsocial);
                    setLocalDomicilio(data.partners[0].domicilio);
                    setLocalTelefono(data.partners[0].telefono);
                    setLocalWhatsapp(data.partners[0].whatsapp);
                    setLocalWeb(data.partners[0].web);
                    setLocalHabilitado(data.partners[0].habilitado);
                    setLocalMostrarWeb(data.partners[0].mostrar_web);
                    setLocalFchCrea(data.partners[0].fchalta);
                    setLocalFchModi(data.partners[0].fchmodificado);
                    setLocalDadoBaja(data.partners[0].dado_baja);
                    setLocalFchBaja(data.partners[0].fchbaja);
                    setLocalLogoMime(data.partners[0].logo_mime);
                    setLocalLogoContent(data.partners[0].logo_content);
                    setLocalLogoName(data.partners[0].logo_name);

                    setErrorPartner(null);
                } else {
                    
                    // Hubo errores al recuperar el Partner
                    setErrorPartner(data.mensaje);
                    setLocalId(0);
                    setPartner(null);
                    setLocalCuit('');
                    setLocalRazonSocial('');
                    setLocalDomicilio('');
                    setLocalTelefono('');
                    setLocalWhatsapp('');
                    setLocalWeb('');
                    setLocalHabilitado('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalDadoBaja('');
                    setLocalFchBaja('');
                    setLocalLogoMime(null);
                    setLocalLogoContent(null);
                    setLocalLogoName(null);
                }

                setIsLoading(false);
                                
            } catch (err) {
                setErrorPartner('Error al obtener la información del Partner.');
                setLocalId(0);
                setPartner(null);
                setLocalCuit('');
                setLocalRazonSocial('');
                setLocalDomicilio('');
                setLocalTelefono('');
                setLocalWhatsapp('');
                setLocalWeb('');
                setLocalHabilitado('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalDadoBaja('');
                setLocalFchBaja('');
                setLocalLogoMime(null);
                setLocalLogoContent(null);
                setLocalLogoName(null);

                setIsLoading(false);
            }
        };

        if (idPartner > 0) {
            // Estoy editando un Partner existente
            fetchPartner();
        } else {
            // Estoy creando un nuevo Partner
            setIsLoading(false);
            setPartner({id:0, cuit:'', razonsocial:'', domicilio:'', telefono:'', whatsapp:'', web:'', habilitado:'', mostrar_web: '', fchalta:'', fchmodificado:'', dado_baja:'', fchbaja:'', logo_mime:null, logo_content:null, logo_name:null, imageSrc: null});
            setImagePreview(null);
            setLocalId(0);
            setLocalCuit('');
            setLocalRazonSocial('');
            setLocalDomicilio('');
            setLocalTelefono('');
            setLocalWhatsapp('');
            setLocalWeb('');
            setLocalFchCrea('');
            setLocalFchModi('');
            setLocalFchBaja('');
            setLocalHabilitado('S');
            setLocalDadoBaja('N');
            setLocalLogoMime(null);
            setLocalLogoContent(null);
            setLocalLogoName(null);
        }

    }, []);

    const handleChangeLocalCuit = (event: React.ChangeEvent<HTMLInputElement>) => {
        // check only numbers and 11 characters
        const value = event.target.value;
        if (/^\d{0,11}$/.test(value)) {
            setLocalCuit(value);
        }
    };

    const handleChangeLocalRazonSocial = (event: React.ChangeEvent<HTMLInputElement>) => {
        // check max 50 characters
        const value = event.target.value.toUpperCase();
        if (value.length <= 50) {
            setLocalRazonSocial(value);
        }
    };

    const handleChangeLocalDomicilio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDomicilio(event.target.value.toUpperCase());
    };

    const handleChangeLocalTelefono = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalTelefono(event.target.value.toUpperCase());
    };

    const handleChangeLocalWhatsapp = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalWhatsapp(event.target.value.toUpperCase());
    };

    const handleChangeLocalWeb = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalWeb(event.target.value);
    };

    const handleChangeLocalLogoMime = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLogoMime(event.target.value);
    };

    const handleChangeLocalLogoContent = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLogoContent(event.target.value);
    };

    const handleChangeLocalLogoName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLogoName(event.target.value);
    };

    const handleChangeLocalHabilitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalMostrarWeb = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalMostrarWeb(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setLocalLogoMime(null);
        setLocalLogoContent(null);
        setLocalLogoName('');
    };

    const handleChangeImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Solo se permiten imágenes");
            return;
        }

        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            const height = img.height;

            if (height > 500) {
                alert(`Máximo permitido: 500px de alto (actual: ${height}px)`);
                URL.revokeObjectURL(objectUrl);
                return;
            }

            setImageFile(file);
            setLocalLogoMime(file.type);
            setLocalLogoName(file.name);

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };

            reader.readAsDataURL(file);
            
            URL.revokeObjectURL(objectUrl);
        };

        img.onerror = () => {
            alert("Error al cargar la imagen");
            URL.revokeObjectURL(objectUrl);
        };

        img.src = objectUrl; // dispara el onload

    };
    
    const guardarCambiosLocal = async () => {

        let logo_name = localLogoName;
        let logo_mime = localLogoMime;
        let logo_content = localLogoContent;

        if (imageFile) {
            const result = await fileToBase64(imageFile);
            logo_name = result.name;
            logo_mime = result.mime;
            logo_content = result.base64;
        } else {
            setLocalLogoMime(Partner?.logo_mime ?? null);
            setLocalLogoContent(Partner?.logo_content ?? null);
            setLocalLogoName(Partner?.logo_name ?? null);
            logo_name = localLogoName;
            logo_mime = localLogoMime;
            logo_content = localLogoContent;
        }

        if (idPartner === 0) {
            // Crear nuevo Partner

            const fechaActual = new Date().toISOString();
            const guardarFecha = fechaActual.slice(0, 10);

            const PartnerNuevo: PartnersEditar = {
                id: 0,
                cuit: localCuit,
                razonsocial: localRazonSocial,
                domicilio: localDomicilio,
                telefono: localTelefono,
                whatsapp: localWhatsapp,
                web: localWeb,
                habilitado: localHabilitado,
                mostrar_web: localMostrarWeb,
                fchalta: guardarFecha,
                fchmodificado: guardarFecha,
                dado_baja: 'N',
                fchbaja: '',
                logo_mime: logo_mime,
                logo_content: logo_content,
                logo_name: logo_name
            };
            
            console.log('Datos a insertar:', PartnerNuevo);
            try {
                const PartnerInsertado = await InsertarPartner(PartnerNuevo);

                if (PartnerInsertado.partner_insertado === 'si') {
                    guardarCambios(PartnerNuevo);
                } else {
                    alert('Error al guardar partner nuevo: ' + PartnerInsertado.mensaje);
                }
            } catch (error) {
                alert('Error al guardar partner nuevo: ' + error);
            }
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica del Partner
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                //const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                const guardarFecha = fechaActual.slice(0, 10);

                // Modificar datos Partner
                const PartnerEditado: PartnersEditar = {
                    id: idPartner,
                    cuit: localCuit,
                    razonsocial: localRazonSocial,
                    domicilio: localDomicilio,
                    telefono: localTelefono,
                    whatsapp: localWhatsapp,
                    web: localWeb,
                    habilitado: 'N',
                    mostrar_web: localMostrarWeb,
                    fchalta: localFchCrea,
                    fchmodificado: guardarFecha,
                    dado_baja: 'S',
                    fchbaja: guardarFecha,
                    logo_mime: localLogoMime,
                    logo_content: localLogoContent,
                    logo_name: localLogoName
                };

                const respuesta = await GuardarPartner(PartnerEditado);

                if (respuesta.partner_guardado === 'si') {
                    guardarCambios(PartnerEditado);
                } else {
                    alert('Eliminar - Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar Partner
                const fechaActual = new Date().toISOString();
                //const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                const guardarFecha = fechaActual.slice(0, 10);

                // Modificar datos Partner
                const PartnerEditado: PartnersEditar = {
                    id: idPartner,
                    cuit: localCuit,
                    razonsocial: localRazonSocial,
                    domicilio: localDomicilio,
                    telefono: localTelefono,
                    whatsapp: localWhatsapp,
                    web: localWeb,
                    habilitado: localHabilitado,
                    mostrar_web: localMostrarWeb,
                    fchalta: localFchCrea,
                    fchmodificado: guardarFecha,
                    dado_baja: localDadoBaja,
                    fchbaja: localDadoBaja === 'S' ? guardarFecha : '',
                    logo_mime: logo_mime,
                    logo_content: logo_content,
                    logo_name: logo_name
                };

                const respuesta = await GuardarPartner(PartnerEditado);

                if (respuesta.partner_guardado === 'si') {
                    guardarCambios(PartnerEditado);
                } else {
                    alert('Modificar - Error al guardar los cambios: ' + respuesta.mensaje);
                    console.log('Datos enviados para guardar:', PartnerEditado);
                    console.log('Respuesta del servidor:', respuesta);
                }
            }
        }
    };
    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && Partner) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminando ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Partner: {Partner?.razonsocial}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idPartner > 0 ? "Editando Partner: " + Partner?.razonsocial : "Nuevo Partner"}</h1>
                    )}
                    {/* Campos principales */}     
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-3 justify-between items-start flex-wrap mb-2 pb-2">
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="id" label="Id" variant="outlined" value={localId}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                disabled
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="cuit" label="Cuit" variant="outlined" value={localCuit}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalCuit}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Razón Social */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="razonsocial" label="Razón Social" variant="outlined" value={localRazonSocial}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalRazonSocial}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Domicilio */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="domicilio" label="Domicilio" variant="outlined" value={localDomicilio}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalDomicilio}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Teléfono */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="telefono" label="Teléfono" variant="outlined" value={localTelefono}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalTelefono}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Whatsapp */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="whatsapp" label="Whatsapp" variant="outlined" value={localWhatsapp}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalWhatsapp}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Web */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="web" label="Web" variant="outlined" value={localWeb}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalWeb}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Nombre archivo */}
                        <div className="flex flex-col justify-start items-center max-w-[200px] flex-grow w-full">
                            <TextField id="nombreArchivo" label="Nombre archivo" variant="outlined" value={localLogoName}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalLogoName}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Fechas */}
                        {Partner.id > 0 && (<>
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcmodi" label="Fec. Modificado" variant="outlined" value={localFchModi}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>                                    
                        </>)
                        }

                        {/* Habilitado */}
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabilitado}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Mostrar_web */}
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-mostrar-web"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Mostrar web
                                    </InputLabel>
                                <Select
                                    labelId="label-mostrar-web"
                                    id="mostrar-web"
                                    value={localMostrarWeb}
                                    label="Mostrar web"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalMostrarWeb}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Dado_baja */}
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {localDadoBaja === 'S' && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchbaja" label="Fec. Baja" variant="outlined" value={localFchBaja}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }

                        {/* Preview y botón para subir imagen nueva */}
                        <div className="flex flex-col justify-start items-center max-w-[220px] flex-grow w-full">

                            {/* Label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    alignSelf: 'flex-start',
                                    marginBottom: '4px',
                                    marginLeft: '4px',
                                    color: 'rgba(0, 0, 0, 0.6)'
                                }}
                            >
                                Logo:<br />(altura máxima permitida: 500px)
                            </Typography>

                            {/* Preview */}
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="logo"
                                    className="w-[120px] h-[120px] object-contain border rounded mb-2"
                                />
                            ) : (
                                <div className="w-[120px] h-[120px] flex items-center justify-center border rounded mb-2 text-gray-400">
                                    Sin imagen
                                </div>
                            )}

                            {/* Botón subir */}
                            <Button
                                variant="contained"
                                component="label"
                                disabled={eliminando}
                                size="small"
                                sx={{ mb: 1 }}
                            >
                                Cambiar
                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={handleChangeImage}
                                />
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={handleRemoveImage}
                                disabled={!imagePreview || eliminando}
                            >
                                Quitar
                            </Button>
                        </div>

                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminando ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};
